// Context-picker state for the SQ-AI chat input.
//
// Holds the chat-session-level scope the user pins via the picker — selected
// modules and (when exactly one module is pinned) a template subset — plus the
// data needed to render it: the tenant's askable modules and each module's
// template list (lazy-loaded). Produces the `context` object sent with every
// turn (chip_context_wiring_spec.md).
//
// State lives here (lifted into SqAiPage) so it persists across turns and is
// injected into each send. It resets when `resetKey` changes — SqAiPage passes
// the conversationId, which a new chat OR any scope change (env/tenant/conf/tz)
// already rotates, so chips clear exactly when the scope they describe does.

import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { aiAssistantApi } from "../../services/api";

// Form-driven modules have templates; tasks are flat records with none
// (B-VERSION-5). Keep in sync with the server's TEMPLATE_TABLES (pickerContext.js).
const MODULES_WITH_TEMPLATES = new Set(["issues", "incidents", "inspections", "risk_assessments", "ptw"]);

// Stable empty array so `templatesFor` returns the same identity when a module has
// no subset (keeps it safe to use directly in render/dep arrays).
const EMPTY_TITLES = [];

export function moduleHasTemplates(canonical) {
  return MODULES_WITH_TEMPLATES.has(canonical);
}

// The four org-hierarchy levels in cascade order. The KEY selects the column the engine
// filters (scope.org.<key>); the per-tenant LABEL + active depth come from the levels
// fetch (node_N_def / is_node_N_active), never assumed here.
const ORG_LEVEL_KEYS = ["facility", "department", "location", "subarea"];

// How long the page waits for the server to say whether continuing a chat is on (P5 — RSM6, the developer's decision of 7 Oct): no
// answer by then reads as off, today's page.
const RESUME_SAID_WAIT_MS = 10000;

// ── Org-hierarchy data-source isolation seam ───────────────────────────────
// usePicker reaches the org tier definitions + cascading options ONLY through these
// two fns. Today they go through the engine's read-only context endpoints; swapping to
// a direct SafetyQube org API later is confined HERE (~20 lines), nothing else changes.
function fetchOrgLevels(env, tenant) {
  return aiAssistantApi.getOrgLevels(env, tenant);
}
function fetchOrgHierarchy(env, tenant, level, parentIds) {
  return aiAssistantApi.getOrgHierarchy(env, tenant, level, parentIds);
}

// The parent level's selected ids for a child level (null for the root). The cascade
// parent is the IMMEDIATELY-PRECEDING active level — for the prefix-active tenants we
// support, that IS the structural FK parent the endpoint queries (department.facility_id,
// location.department_id, sub_area.location_id).
function orgParentIds(levels, selected, levelKey) {
  const idx = levels.findIndex((l) => l.key === levelKey);
  if (idx <= 0) return null; // root (facility) or unknown → no parent
  const parentKey = levels[idx - 1].key;
  return (selected[parentKey] || []).map((it) => it.id);
}

// Cache key for a level's options: the level + its (order-independent) parent id set, so
// changing the parent selection re-fetches but an unchanged set reuses the options.
function orgOptionsCacheKey(levelKey, parentIds) {
  return `${levelKey}|${(parentIds || []).slice().sort((a, b) => a - b).join(",")}`;
}

function errMessage(err, fallback) {
  return (err && err.response && err.response.data && err.response.data.error && err.response.data.error.message) ||
    (err && err.message) || fallback;
}

// Year-to-date (Jan 1 of the current year → today) as YYYY-MM-DD, for the date control's
// DISPLAY default. This matches the engine's YTD default window; computed in the browser's
// local zone (cosmetic — the engine recomputes the actual window in the user's TZ, and an
// untouched picker emits NO date so the engine's own YTD applies). The user picks exact dates.
function ytdDefault() {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return { from: `${now.getFullYear()}-01-01`, to: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}` };
}
// Canonical empty (untouched/cleared) date scope — shows YTD, emits nothing (engine default).
const EMPTY_DATE_SCOPE = { from: null, to: null, option: "raised", set: false };

export function usePicker({ env, tenantSchema, resetKey }) {
  // Available modules for this tenant — labels + disabled already resolved
  // server-side (the picker just renders them).
  const [available, setAvailable] = useState([]);
  const [availableLoading, setAvailableLoading] = useState(false);
  const [availableError, setAvailableError] = useState(null);
  // Whether continuing a saved chat is on, as the SERVER says in this same response (`resumeChats`, docs/DECISIONS.md K-RSM1-1):
  // null until it has said, false where it says false or could not be read — the page shows today's screens until it says true.
  // (…and only a loader while it is null: P5 — RSM6. No answer within RESUME_SAID_WAIT_MS is false, and an answer later than that
  // does not turn continuing on under a page already drawn as off.)
  const [resumeChats, setResumeChats] = useState(null);
  // The server's warning that this account is suspended (`notice`, internal page only), shown as the chat shows it. ACCESS-54
  const [accountNotice, setAccountNotice] = useState(null);

  // Pinned scope. selected = ordered canonical ids; templatesByModule = PER-MODULE
  // template TITLE subsets ({ [canonical]: [titles] }). Kept per module so a
  // comparison scopes EACH module to its own templates — never a single flat list,
  // which would cross-contaminate one module's query with another's template titles.
  const [selected, setSelected] = useState([]);
  const [templatesByModule, setTemplatesByModule] = useState({});
  const [note, setNote] = useState(null);

  // ── Org-hierarchy cascading multi-select scope ──────────────────────────
  // Pinned org levels — GLOBAL (the same ids scope every pinned module, applied per
  // module against its own org columns) and known-id exact (no name resolution). The
  // engine consumes these as scope.org.{facility,department,location,subarea} via the
  // context payload; an empty level injects nothing (facility-only stays unchanged).
  //
  // levels — this tenant's ACTIVE tiers (depth) + per-tenant labels, by position.
  const [orgLevels, setOrgLevels] = useState([]);            // [{ key, position, label }]
  const [orgLevelsLoading, setOrgLevelsLoading] = useState(false);
  const [orgLevelsError, setOrgLevelsError] = useState(null);
  // selected — per-level chosen nodes: { [levelKey]: [{ id, label }] }.
  const [orgSelected, setOrgSelected] = useState({});

  // ── Sticky date filter (From/To + date option) ──────────────────────────
  // The picker's STANDING date range — re-emitted as context.date every turn (the engine
  // threads it as the sticky window: an explicit typed range THIS turn wins, else this range,
  // else the YTD default). `set` distinguishes a user-chosen range (emit it; conflicts with a
  // later typed date trigger the ask-first gate) from the untouched/cleared default (emit
  // NOTHING → the engine's own YTD default applies, SQL byte-identical). The UI SHOWS YTD
  // while unset. option: 'raised' (Raised On / raised_on) | 'occurrence' (Date of Occurrence /
  // incident_occurrence). An ai:context_update {action:'date'} writeback sets it from a typed
  // query the engine resolved (typed → sticky convergence).
  const [dateScope, setDateScope] = useState(EMPTY_DATE_SCOPE);

  // Template lists per module, lazy-loaded + cached for the session. Held in a
  // ref (source of truth) with a render counter so reads during render stay
  // fresh without re-subscribing effects.
  const tplCacheRef = useRef(new Map()); // canonical -> { loading, error, items: [{id,title}] | null }
  const [, bumpTpl] = useReducer((x) => x + 1, 0);

  // Org options per level, lazy-loaded + cached. Keyed by `${levelKey}|${sortedParentIds}`
  // so the cascade re-fetches when the parent selection changes but reuses options for an
  // unchanged parent set. Ref + counter, same pattern as templates.
  const orgOptCacheRef = useRef(new Map()); // cacheKey -> { loading, error, items: [{id,label}] | null }
  const [, bumpOrg] = useReducer((x) => x + 1, 0);

  // Load the tenant's askable modules whenever the scope changes.
  useEffect(() => {
    let saidOff = false;
    const wait = setTimeout(() => {
      saidOff = true;
      setResumeChats((v) => (v === null ? false : v));
    }, RESUME_SAID_WAIT_MS);
    if (!tenantSchema) {
      setAvailable([]);
      setAvailableError(null);
      setResumeChats(null);
      return () => clearTimeout(wait);
    }
    let cancelled = false;
    setAvailableLoading(true);
    setAvailableError(null);
    setResumeChats(null);
    setAccountNotice(null);
    aiAssistantApi
      .getContextModules(env, tenantSchema)
      .then((res) => {
        if (cancelled) return;
        clearTimeout(wait);
        setAvailable(Array.isArray(res.data && res.data.modules) ? res.data.modules : []);
        if (!saidOff) setResumeChats(Boolean(res.data && res.data.resumeChats === true));
        setAccountNotice(res.data && typeof res.data.notice === "string" ? res.data.notice : null);
        setAvailableLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        clearTimeout(wait);
        setAvailable([]);
        setAvailableError(errMessage(err, "Could not load modules"));
        setResumeChats(false);
        setAvailableLoading(false);
      });
    return () => {
      cancelled = true;
      clearTimeout(wait);
    };
  }, [env, tenantSchema]);

  // Load the tenant's org-hierarchy tiers (active depth + per-tenant labels, by position)
  // whenever the tenant changes. Render only ACTIVE levels — a 3-level tenant shows no
  // subarea row. Fail-soft: a load error just leaves no org control (never crashes).
  useEffect(() => {
    if (!tenantSchema) {
      setOrgLevels([]);
      setOrgLevelsError(null);
      return;
    }
    let cancelled = false;
    setOrgLevelsLoading(true);
    setOrgLevelsError(null);
    fetchOrgLevels(env, tenantSchema)
      .then((res) => {
        if (cancelled) return;
        const all = Array.isArray(res.data && res.data.levels) ? res.data.levels : [];
        setOrgLevels(all.filter((l) => l && l.active && ORG_LEVEL_KEYS.includes(l.key)));
        setOrgLevelsLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setOrgLevels([]);
        setOrgLevelsError(errMessage(err, "Could not load org hierarchy"));
        setOrgLevelsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [env, tenantSchema]);

  // The chips as they stand, for carryToNextChat — read from a ref so a socket listener holding an older render's picker still
  // carries what is on screen.
  const chipsNowRef = useRef(null);
  chipsNowRef.current = { selected, templatesByModule, orgSelected, dateScope };
  const carryRef = useRef(null);
  // R3-3b (RSM6, the developer's decision of 7 Oct): a chat started on this page whose first answer was never saved is replaced by a
  // NEW chat with her filters exactly as she had them — modules, each module's templates, sites at every level, dates and their basis.
  // The page calls this just before it starts that chat; the reset below puts them on instead of clearing, once.
  const carryToNextChat = useCallback(() => {
    carryRef.current = chipsNowRef.current;
  }, []);

  // Reset the pinned scope when the chat/scope resets. The tenant's org TIERS persist
  // (they're tenant-scoped, reloaded by the effect above on env/tenant change) — only the
  // SELECTION + cached options clear, so a new chat starts org-unscoped.
  // (…unless the chips were carried to it — carryToNextChat above)
  useEffect(() => {
    const carried = carryRef.current;
    carryRef.current = null;
    setSelected(carried ? carried.selected : []);
    setTemplatesByModule(carried ? carried.templatesByModule : {});
    setOrgSelected(carried ? carried.orgSelected : {});
    setDateScope(carried ? carried.dateScope : EMPTY_DATE_SCOPE); // new chat → date reverts to the YTD default (unset)
    setNote(null);
    tplCacheRef.current = new Map();
    orgOptCacheRef.current = new Map();
    bumpTpl();
    bumpOrg();
  }, [resetKey]);

  // ── Sticky date mutators ─────────────────────────────────────────────────
  // Any explicit edit marks the range `set` (it becomes the standing filter). The engine
  // writeback (setFromEngine) sets it from a typed query's resolved window; an omitted option
  // (a non-picker basis like completed_on) keeps the current option and just updates the range.
  // On the FIRST edit (the unset→set transition), seed the UNTOUCHED endpoint(s) from the
  // currently-DISPLAYED YTD default — otherwise editing only From (or only To) would leave the
  // other bound null and silently emit an open-ended window (unbounded-from/​to), not the
  // bounded YTD range the user was looking at. Subsequent edits operate on the real state.
  const setDateFrom = useCallback((from) => setDateScope((p) => {
    const base = p.set ? p : { ...ytdDefault(), option: p.option };
    return { ...base, from: from || null, set: true };
  }), []);
  const setDateTo = useCallback((to) => setDateScope((p) => {
    const base = p.set ? p : { ...ytdDefault(), option: p.option };
    return { ...base, to: to || null, set: true };
  }), []);
  // Toggling the date OPTION also commits the displayed YTD range as the standing filter (so it
  // emits and the chip is accurate) — and lets "year-to-date by date of occurrence" work without
  // hand-picking dates. On an already-set range it just swaps the basis column.
  const setDateOption = useCallback(
    (option) => setDateScope((p) => {
      const base = p.set ? p : ytdDefault();
      return { ...base, option: option === "occurrence" ? "occurrence" : "raised", set: true };
    }),
    [],
  );
  const clearDate = useCallback(() => setDateScope(EMPTY_DATE_SCOPE), []);
  const setDateFromEngine = useCallback((range) => {
    if (!range || (!range.from && !range.to)) return;
    setDateScope((p) => ({
      from: range.from || null,
      to: range.to || null,
      option: range.option === "occurrence" ? "occurrence" : range.option === "raised" ? "raised" : p.option,
      set: true,
    }));
  }, []);

  // Drop one module's template subset (on removal). Pure helper, no-op if absent.
  const dropModuleTemplates = useCallback((canonical) => {
    setTemplatesByModule((prev) => {
      if (!(canonical in prev)) return prev;
      const next = { ...prev };
      delete next[canonical];
      return next;
    });
  }, []);

  const labelOf = useCallback(
    (canonical) => {
      const m = available.find((x) => x.canonical === canonical);
      return m ? m.label : canonical;
    },
    [available],
  );

  const toggleModule = useCallback(
    (canonical) => {
      const removing = selected.includes(canonical);
      const next = removing ? selected.filter((c) => c !== canonical) : [...selected, canonical];
      setSelected(next);
      // Templates are now PER MODULE, so adding a second module no longer clears
      // anything — each module keeps its own subset. Only drop the subset of a
      // module that was just removed.
      if (removing) dropModuleTemplates(canonical);
      setNote(null);
    },
    [selected, dropModuleTemplates],
  );

  const removeModule = useCallback(
    (canonical) => {
      setSelected((prev) => prev.filter((c) => c !== canonical));
      dropModuleTemplates(canonical);
      setNote(null);
    },
    [dropModuleTemplates],
  );

  // Add one or more modules atomically (a single setSelected) — used by the #2a
  // ADD path (backend "compare X and Y" broadening). A per-call loop of toggleModule
  // would derive each step from the same stale render snapshot and drop all but the
  // last; this handles the whole batch in one update. Per-module templates are
  // PRESERVED — broadening to a comparison no longer clears existing subsets.
  const addModules = useCallback((canonicals) => {
    const list = (Array.isArray(canonicals) ? canonicals : [canonicals]).filter(Boolean);
    setSelected((prev) => {
      const fresh = list.filter((c) => !prev.includes(c));
      return fresh.length ? [...prev, ...fresh] : prev;
    });
  }, []);

  // Set one module's template subset (titles). Empty → the module's entry is removed.
  const setModuleTemplates = useCallback((canonical, titles) => {
    setTemplatesByModule((prev) => {
      const clean = Array.isArray(titles) ? titles.filter((t) => typeof t === "string" && t.trim()) : [];
      if (!clean.length) {
        if (!(canonical in prev)) return prev;
        const next = { ...prev };
        delete next[canonical];
        return next;
      }
      return { ...prev, [canonical]: clean };
    });
  }, []);

  // A continued chat's chips, as the server restored them (ai:resumed — RSM4, design v2 D12): the modules, each module's templates,
  // the org units and the date, in the shape the picker sends, each already checked against what this user is offered today. They
  // REPLACE the selection. The date chip is the saved fixed days, never a relative label; none restored leaves it unset.
  const restore = useCallback((chips) => {
    if (!chips || typeof chips !== "object") return;
    const mods = (Array.isArray(chips.modules) ? chips.modules : []).map((m) => m && m.canonical).filter((c) => typeof c === "string" && c);
    const clean = (titles) => (Array.isArray(titles) ? titles.filter((t) => typeof t === "string" && t.trim()) : []);
    const tbm = {};
    for (const c of mods) {
      const titles = clean(chips.templatesByModule && chips.templatesByModule[c]);
      if (titles.length) tbm[c] = titles;
    }
    // (the flat list names one module's templates only where one module is pinned — the context's own rule)
    if (mods.length === 1 && !tbm[mods[0]] && clean(chips.templates).length) tbm[mods[0]] = clean(chips.templates);
    const org = {};
    for (const key of ORG_LEVEL_KEYS) {
      const raw = chips[key];
      const items = (Array.isArray(raw) ? raw : raw && typeof raw === "object" ? [raw] : [])
        .map((it) => ({ id: Number(it && it.id), label: (it && it.label) || null }))
        .filter((it) => Number.isInteger(it.id));
      if (items.length) org[key] = items;
    }
    const d = chips.date && typeof chips.date === "object" && (chips.date.from || chips.date.to) ? chips.date : null;
    setSelected(mods);
    setTemplatesByModule(tbm);
    setOrgSelected(org);
    setDateScope(d ? { from: d.from || null, to: d.to || null, option: d.option === "occurrence" ? "occurrence" : "raised", set: true } : EMPTY_DATE_SCOPE);
    setNote(null);
  }, []);

  // Read a module's current template subset (stable identity-free helper).
  const templatesFor = useCallback((canonical) => templatesByModule[canonical] || EMPTY_TITLES, [templatesByModule]);

  const dismissNote = useCallback(() => setNote(null), []);

  // Lazy-load a module's templates (once per module, cached for the session).
  const loadTemplates = useCallback(
    (canonical) => {
      if (!moduleHasTemplates(canonical) || !tenantSchema) return;
      const cache = tplCacheRef.current;
      const entry = cache.get(canonical);
      if (entry && (entry.loading || entry.items)) return; // already loading/loaded
      cache.set(canonical, { loading: true, error: null, items: null });
      bumpTpl();
      aiAssistantApi
        .getContextTemplates(env, tenantSchema, canonical)
        .then((res) => {
          cache.set(canonical, {
            loading: false,
            error: null,
            items: Array.isArray(res.data && res.data.templates) ? res.data.templates : [],
          });
          bumpTpl();
        })
        .catch((err) => {
          // items stays null (not []) so the line-above guard treats an errored
          // entry as not-loaded — reopening the panel retries instead of sticking.
          cache.set(canonical, { loading: false, error: errMessage(err, "Could not load templates"), items: null });
          bumpTpl();
        });
    },
    [env, tenantSchema],
  );

  const getTemplateEntry = useCallback((canonical) => tplCacheRef.current.get(canonical) || null, []);

  // Lazy-load a level's options for the CURRENT parent selection (once per level+parents,
  // cached). Root (facility) loads parent-less; a child with no parent selected is a no-op
  // (it stays a "select a <parent> first" placeholder until the parent is chosen).
  const loadOrgOptions = useCallback(
    (levelKey) => {
      if (!tenantSchema) return;
      const idx = orgLevels.findIndex((l) => l.key === levelKey);
      if (idx < 0) return;
      const parentIds = orgParentIds(orgLevels, orgSelected, levelKey);
      if (idx > 0 && (!parentIds || parentIds.length === 0)) return; // child w/o parent → wait
      const key = orgOptionsCacheKey(levelKey, parentIds);
      const cache = orgOptCacheRef.current;
      const entry = cache.get(key);
      if (entry && (entry.loading || entry.items)) return; // already loading/loaded
      cache.set(key, { loading: true, error: null, items: null });
      bumpOrg();
      fetchOrgHierarchy(env, tenantSchema, levelKey, idx === 0 ? undefined : parentIds)
        .then((res) => {
          cache.set(key, {
            loading: false,
            error: null,
            items: Array.isArray(res.data && res.data.options) ? res.data.options : [],
          });
          bumpOrg();
        })
        .catch((err) => {
          // items stays null so the guard above retries on reopen (same as templates).
          cache.set(key, { loading: false, error: errMessage(err, "Could not load options"), items: null });
          bumpOrg();
        });
    },
    [env, tenantSchema, orgLevels, orgSelected],
  );

  // Read a level's options for the current parents: { loading, error, items, ready }.
  // ready=false means a parent level still needs a selection (render a placeholder).
  const getOrgOptions = useCallback(
    (levelKey) => {
      const idx = orgLevels.findIndex((l) => l.key === levelKey);
      if (idx < 0) return { loading: false, error: null, items: [], ready: false };
      const parentIds = orgParentIds(orgLevels, orgSelected, levelKey);
      if (idx > 0 && (!parentIds || parentIds.length === 0)) {
        return { loading: false, error: null, items: [], ready: false };
      }
      const entry = orgOptCacheRef.current.get(orgOptionsCacheKey(levelKey, parentIds));
      // No cache entry yet = ready but the load effect hasn't fired → show as loading, not
      // a one-frame "no options" flash. A completed-empty fetch stores items=[] (not null).
      if (!entry) return { loading: true, error: null, items: [], ready: true };
      return {
        loading: !!entry.loading,
        error: entry.error || null,
        items: entry.items || [],
        ready: true,
      };
    },
    [orgLevels, orgSelected],
  );

  // Toggle one node in a level's selection. Multi-select within the level (→ engine IN).
  // Changing a level ORPHANS every deeper level, so clear their selections (their options
  // re-key on the new parent set and reload on demand).
  const toggleOrgOption = useCallback(
    (levelKey, item) => {
      if (!item || !Number.isInteger(item.id)) return;
      setOrgSelected((prev) => {
        const idx = orgLevels.findIndex((l) => l.key === levelKey);
        if (idx < 0) return prev; // unknown level — never let idx=-1 clobber the whole selection
        const cur = prev[levelKey] || [];
        const exists = cur.some((it) => it.id === item.id);
        const nextLevel = exists
          ? cur.filter((it) => it.id !== item.id)
          : [...cur, { id: item.id, label: item.label }];
        const next = { ...prev, [levelKey]: nextLevel };
        for (let i = idx + 1; i < orgLevels.length; i++) next[orgLevels[i].key] = [];
        return next;
      });
      setNote(null);
    },
    [orgLevels],
  );

  // Clear a level's selection (and every deeper level it orphans).
  const clearOrgLevel = useCallback(
    (levelKey) => {
      setOrgSelected((prev) => {
        const idx = orgLevels.findIndex((l) => l.key === levelKey);
        if (idx < 0) return prev;
        const next = { ...prev };
        for (let i = idx; i < orgLevels.length; i++) next[orgLevels[i].key] = [];
        return next;
      });
      setNote(null);
    },
    [orgLevels],
  );

  const clearOrg = useCallback(() => {
    setOrgSelected({});
    setNote(null);
  }, []);

  // The context object sent with every turn. templatesByModule carries each pinned
  // module's own template subset (authoritative per module). The legacy flat
  // `templates` is still emitted for a single pinned module so any flat consumer
  // keeps working; the server prefers templatesByModule when present.
  const context = useMemo(
    () => {
      const tbm = {};
      for (const c of selected) {
        const titles = templatesByModule[c];
        if (Array.isArray(titles) && titles.length) tbm[c] = titles;
      }
      // Org selection → the engine's scope.org levels (it reads context.{facility,
      // department,location,subarea} per level). `facility` keeps its single-object shape
      // for ONE selection (so the prompt's facility line still renders) and rides as an
      // array for multi-select; deeper levels are always arrays and emitted ONLY when
      // non-empty — an absent level injects no clause, so facility-only / unscoped stays
      // byte-identical to before.
      const ctx = {
        tenant: tenantSchema || null,
        modules: selected.map((c) => ({ canonical: c, label: labelOf(c) })),
        templatesByModule: tbm,
        templates: selected.length === 1 ? (templatesByModule[selected[0]] || []) : [],
        facility: null,
      };
      for (const key of ORG_LEVEL_KEYS) {
        const items = (orgSelected[key] || [])
          .filter((it) => it && Number.isInteger(it.id))
          .map((it) => ({ id: it.id, label: it.label || null }));
        if (!items.length) continue;
        if (key === "facility") ctx.facility = items.length === 1 ? items[0] : items;
        else ctx[key] = items;
      }
      // Sticky date range + option — emitted ONLY when the user has set it (else nothing, so
      // the engine applies its own YTD default and unscoped SQL stays byte-identical). The
      // engine reads context.date.{from,to,option} as the standing window/basis (#2c).
      if (dateScope.set && (dateScope.from || dateScope.to)) {
        ctx.date = { from: dateScope.from || null, to: dateScope.to || null, option: dateScope.option || "raised" };
      }
      return ctx;
    },
    [tenantSchema, selected, templatesByModule, orgSelected, dateScope, labelOf],
  );

  return {
    available,
    availableLoading,
    availableError,
    selected,
    templatesByModule,
    templatesFor,
    note,
    resumeChats,
    accountNotice,
    restore,
    carryToNextChat,
    toggleModule,
    addModules,
    removeModule,
    setModuleTemplates,
    dismissNote,
    loadTemplates,
    getTemplateEntry,
    // Org-hierarchy cascading multi-select — the chip the ContextPicker renders.
    org: {
      levels: orgLevels,
      levelsLoading: orgLevelsLoading,
      levelsError: orgLevelsError,
      selected: orgSelected,
      loadOptions: loadOrgOptions,
      getOptions: getOrgOptions,
      toggleOption: toggleOrgOption,
      clearLevel: clearOrgLevel,
      clearAll: clearOrg,
    },
    // Sticky date filter — the From/To + date-option chip the ContextPicker renders, and the
    // setFromEngine sink the ai:context_update {action:'date'} writeback targets.
    date: {
      value: dateScope,                                              // { from, to, option, set }
      display: dateScope.set ? { from: dateScope.from, to: dateScope.to } : ytdDefault(),
      setFrom: setDateFrom,
      setTo: setDateTo,
      setOption: setDateOption,
      clear: clearDate,
      setFromEngine: setDateFromEngine,
    },
    context,
  };
}
